import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, StickyNote, Share2, X, Check, RotateCcw, ArrowUpRight,
} from 'lucide-react';
import studyApi from '../api/studyProgress';

function useDebouncedCallback(fn, delay) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export default function StudyBar({ itemType, itemSlug, itemTitle, isLoggedIn }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ completed: false, revisit: false, note: '' });
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saved,    setSaved]    = useState(false);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!isLoggedIn || !itemSlug) { setLoading(false); return; }
    studyApi.get(itemType, itemSlug)
      .then(d => { setProgress(d); setNoteText(d.note ?? ''); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [itemType, itemSlug, isLoggedIn]);

  const persistNote = useDebouncedCallback(async (text) => {
    try {
      await studyApi.patch(itemType, itemSlug, { note: text, itemTitle });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch {}
  }, 800);

  const handleNoteChange = (e) => {
    setNoteText(e.target.value);
    persistNote(e.target.value);
  };

  const toggle = async (field) => {
    if (!isLoggedIn) return;
    const next = !progress[field];
    setProgress(p => ({ ...p, [field]: next }));
    try {
      await studyApi.patch(itemType, itemSlug, { [field]: next, itemTitle });
    } catch {
      setProgress(p => ({ ...p, [field]: !next }));
    }
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: itemTitle, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href).catch(() => {});
    }
  };

  if (loading) return null;

  return (
    /* Outer wrapper — full-width on mobile, centered pill on desktop */
    <div
      className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-50 flex flex-col items-stretch sm:items-center gap-2"
      style={{ pointerEvents: 'none' }}
    >
      {/* ── Note panel ── */}
      <AnimatePresence>
        {noteOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            /* Full-width on mobile, fixed 420px on sm+ */
            className="mx-2 sm:mx-0 sm:w-[420px] rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: '#1C1C1E',
              border: '1px solid rgba(255,255,255,0.1)',
              pointerEvents: 'all',
            }}
          >
            {/* header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <StickyNote size={14} className="text-yellow-400" />
                <span className="text-[12px] font-bold text-white">My Notes</span>
              </div>
              <div className="flex items-center gap-2">
                {saved && (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-semibold">
                    <Check size={10} /> Saved
                  </span>
                )}
                <button onClick={() => setNoteOpen(false)} className="text-white/40 hover:text-white transition-colors">
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* textarea */}
            {isLoggedIn ? (
              <textarea
                value={noteText}
                onChange={handleNoteChange}
                placeholder="Write your notes here — key points, mnemonics, anything to remember…"
                rows={6}
                className="w-full bg-transparent text-[13px] text-white/80 placeholder-white/25 px-4 py-3 resize-none outline-none leading-relaxed"
              />
            ) : (
              <div className="px-4 py-6 text-center text-white/40 text-[13px]">
                Please log in to save notes.
              </div>
            )}

            {/* footer */}
            <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-[10px] text-white/25">{noteText.length} characters</span>
              <button
                onClick={() => navigate('/my-notes')}
                className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/80 transition-colors font-semibold"
              >
                All notes <ArrowUpRight size={10} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Action bar ── */}
      <div
        /* Full-width flat bar on mobile, rounded pill on sm+ */
        className="flex items-center sm:rounded-full shadow-2xl"
        style={{
          background: 'rgba(22,22,24,0.97)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          pointerEvents: 'all',
          /* Safe-area padding on mobile for notch/home bar */
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <BarBtn
          onClick={() => toggle('completed')}
          active={progress.completed}
          activeColor="#34D399"
          icon={<CheckCircle2 size={16} />}
          label="Complete"
          disabled={!isLoggedIn}
        />
        <Divider />
        <BarBtn
          onClick={() => toggle('revisit')}
          active={progress.revisit}
          activeColor="#FBBF24"
          icon={<RotateCcw size={16} />}
          label="Revision"
          disabled={!isLoggedIn}
        />
        <Divider />
        <BarBtn
          onClick={() => setNoteOpen(o => !o)}
          active={noteOpen || noteText.length > 0}
          activeColor="#60A5FA"
          icon={<StickyNote size={16} />}
          label="Note"
          badge={noteText.length > 0}
        />
        <Divider />
        <BarBtn
          onClick={share}
          icon={<Share2 size={16} />}
          label="Share"
        />
      </div>
    </div>
  );
}

function BarBtn({ onClick, icon, label, active, activeColor, disabled, badge }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      /* flex-1 on mobile so buttons share equal space; auto on sm+ */
      className="flex-1 sm:flex-none flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-1.5 px-3 sm:px-4 py-3 sm:py-3 text-[10px] sm:text-[12px] font-semibold transition-all relative"
      style={{
        color: active ? activeColor : disabled ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.65)',
      }}
    >
      <span style={{ color: active ? activeColor : 'inherit' }}>{icon}</span>
      <span>{label}</span>
      {badge && (
        <span className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2.5 w-1.5 h-1.5 rounded-full bg-blue-400" />
      )}
    </button>
  );
}

function Divider() {
  return <div className="w-px self-stretch bg-white/10 flex-shrink-0" />;
}
